/**
 * CB Stores — storefront 90% scroll-depth bridge for Shopify Customer Events.
 * Uses Horizon's existing scroll-container utilities (desktop .page-wrapper,
 * mobile document scrolling). Does not contact Google Analytics directly.
 * Requires the Shopify Custom Pixel subscriber for cb_stores:scroll_depth_90.
 */
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const EVENT_NAME = 'cb_stores:scroll_depth_90';
let currentPage = '';
let sentForPage = false;
let framePending = false;
let listeningOn = null;

function pageKey() {
  return window.location.pathname + window.location.search;
}

function checkScrollDepth() {
  framePending = false;

  const key = pageKey();
  if (key !== currentPage) {
    currentPage = key;
    sentForPage = false;
  }
  if (sentForPage) return;

  const container = getScrollContainer();
  if (!container) return;

  const pageHeight = container.scrollHeight;
  const visibleHeight = container.clientHeight;
  const scrollTop = container.scrollTop;

  // Do not count pages that cannot scroll or pages not scrolled by the visitor.
  if (pageHeight <= visibleHeight || scrollTop <= 0) return;
  if ((scrollTop + visibleHeight) / pageHeight < 0.9) return;

  const publish = window.Shopify?.analytics?.publish;
  if (typeof publish !== 'function') return;

  sentForPage = true;
  try {
    const publishResult = publish.call(window.Shopify.analytics, EVENT_NAME, {
      percent_scrolled: 90,
      page_location: window.location.href,
      page_title: document.title || '',
      page_referrer: document.referrer || '',
    });
    if (publishResult && typeof publishResult.catch === 'function') {
      publishResult.catch((error) => {
        sentForPage = false;
        console.warn('[CB Stores] Scroll-depth event rejected', error);
      });
    }
  } catch (error) {
    sentForPage = false;
    console.warn('[CB Stores] Could not publish scroll-depth event', error);
  }
}

function scheduleDepthCheck() {
  if (framePending) return;
  framePending = true;
  requestAnimationFrame(checkScrollDepth);
}

function bindScrollTarget() {
  const target = getScrollEventTarget();
  if (target !== listeningOn) {
    listeningOn?.removeEventListener('scroll', scheduleDepthCheck);
    target.addEventListener('scroll', scheduleDepthCheck, { passive: true });
    listeningOn = target;
  }
  scheduleDepthCheck();
}

function onPageShown() {
  // Allow Horizon's scroll restoration to run before checking restored depth.
  requestAnimationFrame(bindScrollTarget);
}

function initialize() {
  bindScrollTarget();
  window.addEventListener('resize', scheduleDepthCheck, { passive: true });
  window.addEventListener('pageshow', onPageShown);
  window.addEventListener('popstate', scheduleDepthCheck);
  window.addEventListener('hashchange', scheduleDepthCheck);
  document.addEventListener('shopify:section:load', scheduleDepthCheck);
  scrollContainerMediaQuery.addEventListener('change', bindScrollTarget);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
